import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../common/Modal';
import { CreatePurchaseOrderForm } from '../../pages/purchase-orders/CreatePurchaseOrderForm';
import type { PurchaseOrder, PurchaseOrderFormData } from '../../types/purchaseOrder';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { useSettingsStore } from '../../store/settingsStore';
import { formatCurrency } from '../../utils/format';
import { useLanguage } from '../../features/language/useLanguage';

interface PurchaseOrderMiniTableProps {
  supplierId: string;
}

export const PurchaseOrderMiniTable: React.FC<PurchaseOrderMiniTableProps> = ({ supplierId }) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const { currencySymbol } = useSettingsStore();
  const [editingPO, setEditingPO] = useState<PurchaseOrder | null>(null);

  const { data: orders = [], isLoading, isError } = useQuery({
    queryKey: ['purchase-orders', 'supplier', supplierId],
    queryFn: () => purchaseOrderService.getBySupplierId(supplierId),
    enabled: !!supplierId,
  });

  const handleUpdatePO = async (data: PurchaseOrderFormData) => {
    if (!editingPO) return;
    await purchaseOrderService.update(editingPO.id, data, editingPO.orderNumber);
    await queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
  };

  if (isLoading) {
    return <LoadingSpinner text={t('purchaseOrder.table.loading')} />;
  }

  if (isError) {
    return <div className="p-4 text-sm text-red-600">{t('purchaseOrder.table.errorLoad')}</div>;
  }

  if (orders.length === 0) {
    return <div className="p-4 text-sm text-gray-500 italic">{t('purchaseOrder.table.empty')}</div>;
  }

  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 m-2">
      <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('purchaseOrder.table.title')}</h4>
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead>
          <tr>
            <th className="px-4 py-2 text-left font-medium text-gray-500">{t('forms.common.orderNumber')}</th>
            <th className="px-4 py-2 text-center font-medium text-gray-500">{t('reports.general.table.date')}</th>
            <th className="px-4 py-2 text-left font-medium text-gray-500">{t('reports.details.total')}</th>
            <th className="px-4 py-2 text-center font-medium text-gray-500">{t('purchaseOrder.table.orderStatus')}</th>
            <th className="px-4 py-2 text-center font-medium text-gray-500">{t('purchaseOrder.table.paymentStatus')}</th>
            <th className="px-4 py-2 text-center font-medium text-gray-500">{t('purchaseOrder.table.deliveryStatus')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {orders.map(po => (
            <tr
              key={po.id}
              onClick={() => setEditingPO(po)}
              className="hover:bg-gray-100 cursor-pointer"
            >
              <td className="px-4 py-2 font-medium text-gray-900">{po.orderNumber}</td>
              <td className="px-4 py-2 text-gray-500 text-center">{new Date(po.orderDate).toLocaleDateString()}</td>
              <td className="px-4 py-2 text-left text-gray-900">{formatCurrency(po.totalAmount, currencySymbol)}</td>
              <td className="px-4 py-2 text-center">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full uppercase py-1
                    ${po.status === 'draft' ? 'bg-blue-100 text-blue-800' : 
                      po.status === 'ordered' ? 'bg-green-100 text-green-800' : 
                      po.status === 'cancelled' ? 'bg-red-100 text-red-800' : 
                      'bg-gray-100 text-gray-800'}`}
                >
                  {t(`purchaseOrder.filters.options.${po.status}`, undefined, po.status)}
                </span>
              </td>
              <td className="px-4 py-2 text-center">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full uppercase py-1
                    ${po.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' : 
                      po.paymentStatus === 'refunded' ? 'bg-gray-100 text-gray-800' : 
                      'bg-red-100 text-red-800'}`}
                >
                  {t(`purchaseOrder.filters.options.${po.paymentStatus || 'unpaid'}`, undefined, po.paymentStatus || 'unpaid')}
                </span>
              </td>
              <td className="px-4 py-2 text-center">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full uppercase py-1
                    ${po.deliveryStatus === 'received' ? 'bg-green-100 text-green-800' : 
                      po.deliveryStatus === 'shipping' ? 'bg-blue-100 text-blue-800' : 
                      'bg-gray-100 text-gray-800'}`}
                >
                  {t(`purchaseOrder.filters.options.${po.deliveryStatus || 'pending'}`, undefined, po.deliveryStatus || 'pending')}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Modal
        isOpen={!!editingPO}
        onClose={() => setEditingPO(null)}
        title={`${t('purchaseOrder.actions.edit')} ${editingPO?.orderNumber}`}
        maxWidth="5xl"
      >
        {editingPO && (
          <CreatePurchaseOrderForm
            initialData={editingPO}
            onSubmit={handleUpdatePO}
            onCancel={() => setEditingPO(null)}
          />
        )}
      </Modal>
    </div>
  );
};
